import React, { useEffect, useRef, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CSVReader from "react-csv-reader";
import CloseIcon from "@mui/icons-material/Close";
import Modal from "react-modal";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Cookies from "js-cookie";
import axios from "axios";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { CircularProgress } from "@mui/material";
import fallbackImage from "./img/7fd2cdd1-31a7-4810-bf03-137aaea45f12.jpeg";
import Pagination from "./components/Pagination";
import { handleProducts, handleCategoriesTags } from "./utils/productsUtils";

const Product = () => {
  const fileInputRef = useRef(null);
  const [paginatedProducts, setPaginatedProducts] = useState([]);
  const [paginatedProperties, setPaginatedProperties] = useState({
    currentPage: 1,
    from: 0,
    to: 0,
    total: 0,
    last_page: 1,
    first_page_url: "",
    last_page_url: "",
    next_page_url: "",
    prev_page_url: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [screenLoading, setScreenLoading] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [newProductData, setNewProductData] = useState({
    name: "",
    description: "",
    sku: "",
    unit_price: "",
    weight: "",
    length: "",
    width: "",
    height: "",
    categories: "",
    tags: "",
    brand: "",
    luminance: "",
    size: "",
    stock: "",
  });
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [importedData, setImportedData] = useState([]);
  const token = Cookies.get("token");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [addProductModalIsOpen, setAddProductModalIsOpen] = useState(false);
  const [productCategories, setProductCategories] = useState([]);
  const [productTags, setProductTags] = useState([]);
  const [selectedCat, setSelectedCat] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [updatedNavisonStatus, setUpdatedNavisonStatus] = useState("");
  const timeoutRef = useRef(null);

  useEffect(() => {
    handleFetchCatTags();
  }, []);

  useEffect(() => {
    fetchAllProducts();
  }, [currentPage]);

  useEffect(() => {
    const fetchProducts = async () => {
      // Cancel the previous timeout if the effect runs again
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Wait for 3 seconds if there are filters
      if (searchQuery || selectedCat?.length > 0 || selectedTags?.length > 0) {
        timeoutRef.current = setTimeout(async () => {
          setCurrentPage(1);
          const gottenProducts = await handleProducts(
            currentPage,
            searchQuery,
            selectedCat,
            selectedTags
          );
          if (gottenProducts) {
            setPaginatedProducts(gottenProducts.product.data);
            setPaginatedProperties({
              total: gottenProducts.product.total,
              last_page: gottenProducts.product.last_page,
            });
          } else {
            console.log("Failed to fetch products.");
          }
        }, 2000); // Wait for 3 seconds before fetching
      } else {
        // If no filters, fetch products immediately
        const gottenProducts = await handleProducts(
          currentPage,
          searchQuery,
          selectedCat,
          selectedTags
        );
        if (gottenProducts) {
          setCurrentPage(1);
          setPaginatedProducts(gottenProducts.product.data);
          setPaginatedProperties({
            total: gottenProducts.product.total,
            last_page: gottenProducts.product.last_page,
          });
        } else {
          console.log("Failed to fetch products.");
        }
      }
    };

    fetchProducts();
    return () => {
      // Cleanup on component unmount or when the effect runs again
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [searchQuery, selectedCat, selectedTags]);

  const handleSyncProduct = async () => {
    setUpdatedNavisonStatus("Syncing...");
    try {
      const token = Cookies.get("token");
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/syncProduct",
        {
          id: selectedProduct.id,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status !== 200) {
        setUpdatedNavisonStatus("Sync Failed! Network Error.");
      }

      const data = await response.data;

      setUpdatedNavisonStatus(data.navisonStatus);
    } catch (err) {
      setUpdatedNavisonStatus(err.toString());
    }
  };

  const handleFetchCatTags = async () => {
    const data = await handleCategoriesTags();
    if (data) {
      setProductCategories(data.categories);
      setProductTags(data.tags);
    }
  };

  const fetchAllProducts = async () => {
    // If no filters, fetch products immediately
    const gottenProducts = await handleProducts(
      currentPage,
      searchQuery,
      selectedCat,
      selectedTags
    );
    if (gottenProducts) {
      setPaginatedProducts(gottenProducts.product.data);
      setPaginatedProperties({
        total: gottenProducts.product.total,
        last_page: gottenProducts.product.last_page,
      });
    } else {
      console.log("Failed to fetch products.");
    }
  };

  const handleImageUpload = async (e) => {
    setFileUploading(true);
    const file = e.target.files;
    console.log(e.target.files);

    const formData = new FormData();

    for (let i = 0; i < file.length; i++) {
      formData.append("files[]", file[i]);
    }

    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/upload-image",
        formData
      );
      const imageUrl = response.data.urls;
      const updatedProduct = { ...selectedProduct };
      updatedProduct.img_src = imageUrl[0];
      console.log(imageUrl[0]);
      setSelectedProduct(updatedProduct);
      setFileUploading(false);
    } catch (error) {
      console.log(error);
      setFileUploading(false);
    }
  };

  const handleClick = () => {
    const fileInput = document.getElementById("imageInput");
    fileInput.click();
  };

  const handleNewProductImageUpload = async (e) => {
    setFileUploading(true);
    const file = e.target.files;
    console.log(e.target.files);

    const formData = new FormData();

    for (let i = 0; i < file.length; i++) {
      formData.append("files[]", file[i]);
    }

    try {
      const response = await axios.post(
        process.env.REACT_APP_API_URL + "/upload-image",
        formData
      );
      const imageUrl = response.data.urls;
      const updatedProduct = { ...newProductData };
      updatedProduct.img_src = imageUrl[0];
      console.log(imageUrl[0]);
      setNewProductData(updatedProduct);
      setFileUploading(false);
    } catch (error) {
      console.log(error);
      setFileUploading(false);
    }
  };

  const handleNewProductClick = () => {
    const fileInput = document.getElementById("imageNewProductInput");
    fileInput.click();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedProduct({ ...selectedProduct, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const updatedAllProduct = [];
    updatedAllProduct.push(selectedProduct);

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/createandupdateproducts",
        {
          body: JSON.stringify({
            product_data: updatedAllProduct,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          method: "POST",
        }
      );
      if (!response.ok) {
        // setSignUpError(true);
        throw new Error("Network response was not ok");
      }

      // Clear the form data after adding the product
    } catch (error) {
      // setSignUpError(true);
      console.error("Error signing up:", error);
    }
    setSearchQuery(null);
    setSelectedCat(null);
    setSelectedTags(null);
    fetchAllProducts();

    setIsFormVisible(false);
  };

  const handleForce = (data) => {
    setImportedData(data);
    setModalIsOpen(true);
  };

  const handleProcessImportedData = (data) => {
    if (data && data.length > 0) {
      processCsvData(data);
    }
  };

  const processCsvData = async (importedProducts) => {
    setScreenLoading(true);
    // Define the maximum chunk size
    const chunkSize = 10;

    // Create a new array to hold the updated products
    const updatedAllProduct = [];

    // Split the imported products into chunks
    for (let i = 0; i < importedProducts.length; i += chunkSize) {
      const chunk = importedProducts.slice(i, i + chunkSize);

      // Iterate over the chunk of products
      chunk.forEach((importedProduct) => {
        // Check if the sku of the imported product already exists in the allProduct array
        const existingProductIndex = updatedAllProduct.findIndex(
          (product) => product.sku === importedProduct.sku
        );

        if (importedProduct.width === null) {
          console.log("the product", importedProduct.sku, "is null on name");
        }

        // If the product with the same sku exists, update its properties
        if (existingProductIndex !== -1) {
          updatedAllProduct[existingProductIndex] = {
            ...updatedAllProduct[existingProductIndex],
            ...importedProduct,
          };
        } else {
          // If the product with the same sku does not exist, add it as a new product
          updatedAllProduct.push(importedProduct);
          console.log("product being pushed");
        }
      });

      // Make an API call for the current chunk of products
      await updateProductsViaAPI(chunk);
    }

    // refresh
    setSearchQuery(null);
    setSelectedCat(null);
    setSelectedTags(null);
    fetchAllProducts();
    setScreenLoading(false);
  };

  const updateProductsViaAPI = async (products) => {
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/createandupdateproducts",
        {
          body: JSON.stringify({
            product_data: products,
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          method: "POST",
        }
      );
      if (!response.ok) {
        // Handle API error here
        console.error("API Error:", response.statusText);
        throw new Error("Network response was not ok");
      }
    } catch (error) {
      // Handle error
      console.error("Error updating products:", error);
    }
  };

  const handleImportButtonClick = () => {
    // Trigger the file input when the custom import button is clicked
    fileInputRef.current.click();
  };

  const handleNewProductInputChange = (e) => {
    const { name, value } = e.target;
    setNewProductData({ ...newProductData, [name]: value });
  };

  const handleAddNewProduct = async (e) => {
    e.preventDefault();
    const sendingProductData = { ...newProductData };
    sendingProductData.sku = sendingProductData.name;
    setNewProductData(sendingProductData);
    // Add the new product to the allProduct array

    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + "/createandupdateproducts",
        {
          body: JSON.stringify({
            product_data: [sendingProductData],
          }),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          method: "POST",
        }
      );
      if (!response.ok) {
        // setSignUpError(true);
        throw new Error("Network response was not ok");
      }
      const json = await response.json();
      console.log(json.updated_products);
      // setAllProduct([...allProduct, json.updated_products[0]]);
      // Clear the form data after adding the product

      setSearchQuery(null);
      setSelectedCat(null);
      setSelectedTags(null);
      fetchAllProducts();
    } catch (error) {
      // setSignUpError(true);
      console.error("Error signing up:", error);
    }

    // Clear the form data after adding the product
    setNewProductData({
      name: "",
      description: "",
      sku: "",
      unit_price: "",
      weight: "",
      length: "",
      width: "",
      height: "",
      categories: "",
      tags: "",
      brand: "",
      luminance: "",
      size: "",
      stock: "",
    });
    setAddProductModalIsOpen(false);
  };

  return (
    <div style={{ position: "relative" }}>
      {screenLoading ? (
        <div
          style={{
            position: "fixed",
            width: "87vw",
            height: "100vh",
            background: "rgba(0,0,0,0.8)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress
            color="primary"
            size={100}
            style={{ color: "white" }}
          />
        </div>
      ) : (
        <></>
      )}
      <Modal
        isOpen={deleteIsOpen}
        ariaHideApp={false}
        onRequestClose={() => {
          setDeleteIsOpen(false);
        }}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "600px",
            height: "300px",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            boxShadow: "none",
            borderRadius: "10px",
            border: "none",
            overflowX: "hidden",
            overflowY: "auto",
            padding: "none",
            display: "flex",
            textAlign: "center",
          },
        }}
      >
        <div
          style={{
            fontSize: "35px",
            color:
              process.env.REACT_APP_TYPE !== "OASIS"
                ? "rgb(209, 174, 110)"
                : "rgb(191, 19, 42)",
            fontWeight: "600",
            marginBottom: "20px",
          }}
        >
          Confirm delete product?
        </div>
        <div style={{ textAlign: "center" }}>
          <button
            style={{
              background: "white",
              padding: "8px 25px",
              color:
                process.env.REACT_APP_TYPE !== "OASIS"
                  ? "rgb(209, 174, 110)"
                  : "rgb(191, 19, 42)",
              cursor: "pointer",
              borderRadius: "10px",
              border: "1px solid #d1ae6e",
              outline: "none",
              fontSize: "18px",
              marginRight: "10px",
            }}
            onClick={() => {
              setDeleteIsOpen(false);
            }}
          >
            Cancel
          </button>
          <button
            style={{
              background:
                process.env.REACT_APP_TYPE !== "OASIS"
                  ? "rgb(209, 174, 110)"
                  : "rgb(191, 19, 42)",
              padding: "8px 25px",
              color: "white",
              cursor: "pointer",
              borderRadius: "10px",
              border: "none",
              outline: "none",
              fontSize: "18px",
            }}
            onClick={async () => {
              const productIndex = paginatedProducts.findIndex(
                (finding) => finding.sku === selectedProduct.sku
              );
              const changingProduct = [...paginatedProducts];
              const token = Cookies.get("token");
              try {
                const response = await axios.delete(
                  `https://www.sales.gclightinggallery.com/laravel/api/deleteproducts/${selectedProduct.id}`,
                  {
                    headers: {
                      Authorization: `Bearer ${token}`,
                    },
                  }
                );
                // changingProduct.splice(productIndex, 1);
                // setAllProduct(changingProduct);
                setSearchQuery(null);
                setSelectedCat(null);
                setSelectedTags(null);
                fetchAllProducts();
                setDeleteIsOpen(false);
                setIsFormVisible(false);
              } catch (error) {
                console.log(error);
                // setAllProduct(changingProduct);
                setDeleteIsOpen(false);
                setIsFormVisible(false);
              }
            }}
          >
            Delete Product
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={modalIsOpen}
        ariaHideApp={false}
        onRequestClose={() => {
          setModalIsOpen(false);
          fileInputRef.current.value = "";
        }}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            height: "90%",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            boxShadow: "none",
            borderRadius: "10px",
            border: "none",
            overflowX: "hidden",
            overflowY: "auto",
            padding: "none",
          },
        }}
      >
        <div
          style={{
            width: "100%",
            height: "90%",
          }}
        >
          <div
            style={{
              fontSize: "30px",
              fontweight: "600",
              color: "rgba(0,0,0,0.7)",
              borderBottom: "1px solid lightgrey",
              padding: "10px 0px",
              width: "97%",
              marginLeft: "1.5%",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ marginLeft: "10px" }}>Product Import List</div>
            <div style={{ marginRight: "10px" }}>
              <CloseIcon
                style={{ fontSize: "28px", cursor: "pointer" }}
                onClick={() => {
                  setModalIsOpen(false);
                  fileInputRef.current.value = "";
                }}
              />
            </div>
          </div>
          {importedData.length > 0 ? (
            <div
              style={{
                width: "97%",
                padding: "0 1.5%",
                display: "flex",
                flexDirection: "column",
                height: "calc(100% - 100px)",
                overflowY: "scroll",
              }}
            >
              {importedData.map((product, index) => (
                <div
                  key={index}
                  className="invoice_item"
                  style={{
                    borderBottom: "1px solid lightgrey",
                    padding: "20px",
                    height: "auto",
                  }}
                >
                  <div
                    style={{
                      //   background: "rgba(256,256,256,0.8)",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "auto",
                        paddingRight: "1%",
                      }}
                    >
                      <img
                        src={product.img_src}
                        onError={(e) => {
                          e.target.src = fallbackImage;
                        }}
                        style={{
                          height: "80px",
                          width: "80px",
                          objectFit: "cover",
                          border: "1px solid lightgrey",
                          borderRadius: "10px",
                          marginRight: "5px",
                        }}
                      />
                    </div>
                    <div style={{ width: "90%", paddingRight: "1%" }}>
                      <div
                        style={{
                          fontweight: "600",
                          color:
                            process.env.REACT_APP_TYPE !== "OASIS"
                              ? "rgb(209, 174, 110)"
                              : "rgb(191, 19, 42)",
                          fontSize: "20px",
                        }}
                      >
                        {product.name}
                      </div>
                      <div
                        style={{ fontweight: "400", color: "rgba(0,0,0,0.6)" }}
                      >
                        {product.description}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex" }}>
                    <div
                      style={{
                        width: "24%",
                        paddingRight: "1%",
                        display: "flex",
                      }}
                    >
                      {product.stock > 10 ? (
                        <div style={{ color: "#0d8c22", fontweight: "600" }}>
                          In stock
                        </div>
                      ) : product.stock <= 10 && product.stock > 0 ? (
                        <div style={{ color: "#e62e2e", fontweight: "600" }}>
                          Low stock
                        </div>
                      ) : product.stock === 0 ? (
                        <div style={{ color: "#e62e2e", fontweight: "600" }}>
                          No stock
                        </div>
                      ) : (
                        <></>
                      )}

                      <div style={{ marginLeft: "5px" }}>({product.stock})</div>
                    </div>
                    <div style={{ width: "24%", paddingRight: "1%" }}>
                      {product.categories}
                    </div>

                    <div style={{ width: "24%", paddingRight: "1%" }}>
                      {product.tags}
                    </div>
                    <div
                      style={{
                        width: "24%",
                        paddingRight: "1%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "20px",
                          height: "20px",
                          background: product.luminance,
                          borderRadius: "50%",
                          marginRight: "5px",
                          boxShadow: "0px 0px 8px rgba(0,0,0,0.2)",
                        }}
                      ></div>
                      <div>{product.luminance}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>no data</>
          )}
        </div>
        <div style={{ textAlign: "center" }}>
          <button
            style={{
              background: "white",
              padding: "8px 25px",
              color:
                process.env.REACT_APP_TYPE !== "OASIS"
                  ? "rgb(209, 174, 110)"
                  : "rgb(191, 19, 42)",
              cursor: "pointer",
              borderRadius: "10px",
              border: "1px solid #d1ae6e",
              outline: "none",
              fontSize: "18px",
              marginRight: "10px",
            }}
            onClick={() => {
              setModalIsOpen(false);
              fileInputRef.current.value = "";
            }}
          >
            Cancel
          </button>
          <button
            style={{
              background:
                process.env.REACT_APP_TYPE !== "OASIS"
                  ? "rgb(209, 174, 110)"
                  : "rgb(191, 19, 42)",
              padding: "8px 25px",
              color: "white",
              cursor: "pointer",
              borderRadius: "10px",
              border: "none",
              outline: "none",
              fontSize: "18px",
            }}
            onClick={() => {
              handleProcessImportedData(importedData);
              setModalIsOpen(false);
              fileInputRef.current.value = "";
            }}
          >
            Import
          </button>
        </div>
      </Modal>
      <Modal
        ariaHideApp={false}
        isOpen={addProductModalIsOpen}
        onRequestClose={() => {
          setAddProductModalIsOpen(false);
          setNewProductData({
            name: "",
            description: "",
            sku: "",
            unit_price: "",
            weight: "",
            length: "",
            width: "",
            height: "",
            categories: "",
            tags: "",
            brand: "",
            luminance: "",
            size: "",
            stock: "",
          });
        }}
        style={{
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          content: {
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "700px",
            height: "800px",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "white",
            boxShadow: "none",
            borderRadius: "10px",
            border: "none",
            overflowX: "hidden",
            overflowY: "auto",
            padding: "none",
          },
        }}
      >
        <div
          style={{
            fontSize: "25px",
            fontweight: "600",
            color: "rgba(0,0,0,0.7)",
            borderBottom: "1px solid lightgrey",
            padding: "10px 0px",
            width: "97%",
            marginLeft: "1.5%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{ marginLeft: "10px", fontSize: "25px", fontweight: "600" }}
          >
            New Product
          </div>
          <div style={{ marginRight: "10px" }}>
            <CloseIcon
              style={{ fontSize: "28px", cursor: "pointer" }}
              onClick={() => {
                setAddProductModalIsOpen(false);
                setNewProductData({
                  name: "",
                  description: "",
                  sku: "",
                  unit_price: "",
                  weight: "",
                  length: "",
                  width: "",
                  height: "",
                  categories: "",
                  tags: "",
                  brand: "",
                  luminance: "",
                  size: "",
                  stock: "",
                });
              }}
            />
          </div>
        </div>
        <div style={{ width: "94%", padding: "0 3%" }}>
          <form onSubmit={handleAddNewProduct}>
            <div
              style={{ marginRight: "25px", marginTop: "20px" }}
              onClick={handleNewProductClick}
            >
              <input
                id="imageNewProductInput"
                type="file"
                style={{ display: "none" }}
                onChange={handleNewProductImageUpload}
              />
              {fileUploading ? (
                <div
                  style={{
                    boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                    borderRadius: "10px",
                    width: "180px",
                    height: "180px",
                    objectFit: "cover",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <CircularProgress color="primary" size={50} style={{}} />
                </div>
              ) : newProductData.img_src ? (
                <img
                  src={newProductData.img_src}
                  onError={(e) => {
                    e.target.src = fallbackImage;
                  }}
                  style={{
                    boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                    borderRadius: "10px",
                    width: "180px",
                    height: "180px",
                    objectFit: "cover",
                    cursor: "pointer",
                  }}
                />
              ) : (
                <div
                  style={{
                    boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                    borderRadius: "10px",
                    width: "180px",
                    height: "180px",
                    objectFit: "cover",
                    cursor: "pointer",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <FileUploadIcon
                    style={{ color: "lightgrey", fontSize: "50px" }}
                  />
                </div>
              )}
            </div>
            <div style={{ width: "100%", marginTop: "10px" }}>
              <div>
                <input
                  required
                  type="text"
                  name="name"
                  value={newProductData.name}
                  onChange={handleNewProductInputChange}
                  placeholder="Product Name"
                  style={{
                    fontSize: "18px",
                    background: "none",
                    border: "1px solid lightgrey",
                    outline: "none",
                    borderRadius: "10px",
                    width: "100%",
                    color: "rgba(0,0,0,0.7)",
                    fontweight: "600",
                    padding: "5px",
                  }}
                />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {/* <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    width: "50%",
                  }}
                >
                  <div style={{ marginRight: "5px" }}>SKU: </div>
                  <input
                    required
                    type="text"
                    name="sku"
                    placeholder="LB0123456789"
                    value={newProductData.sku}
                    onChange={handleNewProductInputChange}
                    style={{
                      fontSize: "18px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "200px",
                      color: "rgba(0,0,0,0.7)",
                      marginTop: "15px",
                      fontweight: "600",
                      padding: "5px 10px",
                    }}
                  />
                </div> */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    width: "50%",
                  }}
                >
                  <div style={{ marginRight: "5px" }}>SGD </div>
                  <input
                    required
                    type="text"
                    name="unit_price"
                    placeholder="0.00"
                    value={newProductData.unit_price}
                    onChange={handleNewProductInputChange}
                    style={{
                      fontSize: "18px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "100px",
                      color: "rgba(0,0,0,0.7)",
                      marginTop: "15px",
                      fontweight: "600",
                      padding: "5px 10px",
                    }}
                  />
                </div>
              </div>
              <div>
                <textarea
                  required
                  type="text"
                  name="description"
                  placeholder="Type your product description here."
                  value={newProductData.description}
                  onChange={handleNewProductInputChange}
                  style={{
                    fontSize: "15px",
                    background: "none",
                    border: "1px solid lightgrey",
                    outline: "none",
                    borderRadius: "10px",
                    width: "100%",
                    color: "rgba(0,0,0,0.7)",
                    fontweight: "400",
                    marginTop: "15px",
                    height: "120px",
                    padding: "5px",
                  }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ marginRight: "5px", width: "180px" }}>Stock:</div>
                <input
                  type="text"
                  name="stock"
                  placeholder=""
                  value={newProductData.stock}
                  onChange={handleNewProductInputChange}
                  style={{
                    fontSize: "15px",
                    background: "none",
                    border: "1px solid lightgrey",
                    outline: "none",
                    borderRadius: "10px",
                    width: "100px",
                    color: "rgba(0,0,0,0.7)",
                    fontweight: "400",
                    marginTop: "15px",
                    padding: "5px",
                    marginRight: "5px",
                  }}
                />
                {newProductData.stock ? (
                  <div
                    className="blink"
                    style={{
                      width: "10px",
                      height: "10px",
                      background: newProductData.stock < 10 ? "red" : "#0d8c22",
                      borderRadius: "50%",
                      boxShadow:
                        newProductData.stock < 10
                          ? "0px 0px 8px red"
                          : "0px 0px 8px #0d8c22",
                    }}
                  />
                ) : (
                  <></>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ marginRight: "5px", width: "180px" }}>
                  Color/Luminance:
                </div>
                <select
                  type="text"
                  name="luminance"
                  value={newProductData.luminance}
                  onChange={handleNewProductInputChange}
                  style={{
                    fontSize: "15px",
                    background: "none",
                    border: "1px solid lightgrey",
                    outline: "none",
                    borderRadius: "10px",
                    width: "250px",
                    color: "rgba(0,0,0,0.7)",
                    fontweight: "400",
                    marginTop: "15px",
                    padding: "5px",
                  }}
                >
                  <option value="None">None</option>
                  <option value="Blue">9,000 K</option>
                  <option value="Lightblue">6,500 K</option>
                  <option value="white">5,000 K</option>
                  <option value="Lightyellow">4,100 K</option>
                  <option value="Yellow">3,000 K</option>
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ marginRight: "5px", width: "180px" }}>
                  Categories:
                </div>
                <input
                  type="text"
                  name="categories"
                  placeholder="Input categories to ease search results"
                  value={newProductData.categories}
                  onChange={handleNewProductInputChange}
                  style={{
                    fontSize: "15px",
                    background: "none",
                    border: "1px solid lightgrey",
                    outline: "none",
                    borderRadius: "10px",
                    width: "600px",
                    color: "rgba(0,0,0,0.7)",
                    fontweight: "400",
                    marginTop: "15px",
                    padding: "5px",
                  }}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <div style={{ marginRight: "5px", width: "180px" }}>Tags:</div>
                <input
                  type="text"
                  name="tags"
                  placeholder="tags your product to ease search results"
                  value={newProductData.tags}
                  onChange={handleNewProductInputChange}
                  style={{
                    fontSize: "15px",
                    background: "none",
                    border: "1px solid lightgrey",
                    outline: "none",
                    borderRadius: "10px",
                    width: "600px",
                    color: "rgba(0,0,0,0.7)",
                    fontweight: "400",
                    marginTop: "15px",
                    padding: "5px",
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              style={{
                background:
                  process.env.REACT_APP_TYPE !== "OASIS"
                    ? "rgb(209, 174, 110)"
                    : "rgb(191, 19, 42)",
                padding: "8px 25px",
                color: "white",
                cursor: "pointer",
                borderRadius: "10px",
                border: "none",
                outline: "none",
                fontSize: "18px",
                marginTop: "40px",
              }}
            >
              Add Product
            </button>
          </form>
        </div>
      </Modal>

      <div
        style={{
          padding: "0px 20px",
          height: "70px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: isFormVisible ? "30px" : "0px",
            marginTop: "7px",
            opacity: isFormVisible ? "1" : "0",
            transition: "all 0.4s ease-in-out",
            cursor: isFormVisible ? "pointer" : "none",
          }}
          onClick={() => setIsFormVisible(false)}
        >
          <ArrowBackIosNewIcon />
        </div>
        <div
          style={{
            margin: "0px",
            fontSize: "32PX",
            fontWeight: "700",
            marginRight: "10px",
            cursor: "pointer",
          }}
          onClick={() => setIsFormVisible(false)}
        >
          Products
        </div>
        <div
          style={{
            background:
              process.env.REACT_APP_TYPE !== "OASIS"
                ? "rgb(209, 174, 110)"
                : "rgb(191, 19, 42)",
            borderRadius: "7px",
            display: "flex",
            alignItems: "center",
            padding: "6px 15px 6px 10px",
            cursor: "pointer",
            marginRight: "5px",
            marginTop: "5px",
          }}
          onClick={() => setAddProductModalIsOpen(true)}
        >
          <AddIcon style={{ color: "white", fontSize: "18px" }} />
          <div
            style={{
              color: "white",
              fontSize: "13px",
              marginBottom: "2.5px",
            }}
          >
            Add New
          </div>
        </div>
        <div
          style={{
            background:
              process.env.REACT_APP_TYPE !== "OASIS"
                ? "rgb(209, 174, 110)"
                : "rgb(191, 19, 42)",
            borderRadius: "7px",
            display: "flex",
            alignItems: "center",
            padding: "6px 15px 6px 10px",
            cursor: "pointer",
            marginRight: "5px",
            marginTop: "5px",
          }}
          onClick={handleImportButtonClick}
        >
          <AddIcon style={{ color: "white", fontSize: "18px" }} />
          <div
            style={{
              color: "white",
              fontSize: "13px",
              marginBottom: "2.5px",
            }}
          >
            Import Products
          </div>
        </div>
        {/* <div
          style={{
            background: process.env.REACT_APP_TYPE !== "OASIS" ? "rgb(209, 174, 110)" : "rgb(191, 19, 42)",
            borderRadius: "7px",
            display: "flex",
            alignItems: "center",
            padding: "6px 15px 6px 10px",
            cursor: "pointer",
            marginRight: "5px",
            marginTop: "5px",
          }}
        >
          <AddIcon style={{ color: "white", fontSize: "18px" }} />
          <div
            style={{
              color: "white",
              fontSize: "13px",
              marginBottom: "2.5px",
            }}
          >
            Export Products
          </div>
        </div> */}
        <div style={{ display: "none" }}>
          <CSVReader
            ref={fileInputRef}
            cssClass="react-csv-input"
            label="Select CSV File"
            onFileLoaded={handleForce}
            parserOptions={{
              header: true,
              skipEmptyLines: true,
            }}
          />
        </div>
      </div>

      {isFormVisible ? (
        <></>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              width: "calc(100% - 40px)",
              borderTop: "1px solid lightgrey",
              padding: "5px 20px",
              height: "30px",
              alignItems: "center",
            }}
          >
            <input
              className="search_input"
              placeholder="Search all Products"
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
              }}
              style={{
                padding: "8px 20px",
                width: "400px",
                border: "none",
                background: "#f0eeeb",
                borderRadius: "8px",
                marginRight: "15px",
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginRight: "10px",
                cursor: "pointer",
              }}
              onClick={() => {
                // setSearchQuery("");
                setIsFormVisible(false);
              }}
            >
              <div
                style={{
                  marginRight: "3px",
                  color:
                    process.env.REACT_APP_TYPE !== "OASIS"
                      ? "rgb(209, 174, 110)"
                      : "rgb(191, 19, 42)",
                }}
              >
                All ({paginatedProperties.total})
              </div>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "calc(100% - 40px)",
              borderTop: "1px solid lightgrey",
              padding: "5px 20px",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: 700 }}>Product Tags:</div>
            <div>
              {productTags.map((filter) => (
                <button
                  key={filter}
                  style={{
                    margin: "0 2px",
                    background: selectedTags?.includes(filter)
                      ? process.env.REACT_APP_TYPE !== "OASIS"
                        ? "rgb(209, 174, 110)"
                        : "rgb(191, 19, 42)"
                      : "white",
                    color: selectedTags?.includes(filter)
                      ? "white"
                      : "rgba(0,0,0,0.7)",
                    border: "none",
                    padding: "5px 15px",
                    borderRadius: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    let localTags = [...selectedTags];
                    if (!selectedTags?.includes(filter)) {
                      localTags.push(filter);
                      setSelectedTags(localTags);
                    } else {
                      localTags = selectedTags.filter(
                        (eachTag) => eachTag != filter
                      );
                      setSelectedTags(localTags);
                    }
                  }}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              width: "calc(100% - 40px)",
              borderTop: "1px solid lightgrey",
              padding: "5px 20px",
              alignItems: "center",
            }}
          >
            <div style={{ fontWeight: 700 }}>Product Categories:</div>
            <div style={{ overflowX: "scroll", display: "flex" }}>
              {productCategories.map((filter) => (
                <button
                  key={filter}
                  style={{
                    margin: "0 2px",
                    background: selectedCat?.includes(filter)
                      ? process.env.REACT_APP_TYPE !== "OASIS"
                        ? "rgb(209, 174, 110)"
                        : "rgb(191, 19, 42)"
                      : "white",
                    color: selectedCat?.includes(filter)
                      ? "white"
                      : "rgba(0,0,0,0.7)",
                    border: "none",
                    padding: "5px 15px",
                    borderRadius: "10px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    let localCat = [...selectedCat];
                    if (!selectedCat?.includes(filter)) {
                      localCat.push(filter);
                      setSelectedCat(localCat);
                    } else {
                      localCat = selectedCat.filter(
                        (eachCat) => eachCat != filter
                      );
                      setSelectedCat(localCat);
                    }
                  }}
                >
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
      {/* <div>{selectedFilters}</div> */}

      {isFormVisible ? (
        <div
          style={{
            width: "100%",
            borderTop: "1px solid lightgrey",
            padding: "20px",
            background: "#f7f6f5",
            height: "calc(100vh - 100px)",
            overflowY: "scroll",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <div
              style={{
                color:
                  process.env.REACT_APP_TYPE !== "OASIS"
                    ? "rgb(209, 174, 110)"
                    : "rgb(191, 19, 42)",
                fontSize: "25px",
                fontweight: "600",
                marginRight: "15px",
              }}
            >
              {selectedProduct.name}
            </div>
            <div
              style={{
                color: "rgba(0,0,0,0.4)",
                fontSize: "15px",
                fontweight: "400",
              }}
            >
              {selectedProduct.sku}
            </div>
          </div>
          <form onSubmit={handleSubmit}>
            <div
              style={{
                display: "flex",
                width: "calc(98% - 40px)",
                marginTop: "25px",
              }}
            >
              <div style={{ marginRight: "25px" }} onClick={handleClick}>
                <input
                  id="imageInput"
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleImageUpload}
                />
                {fileUploading ? (
                  <div
                    style={{
                      boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                      borderRadius: "10px",
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <CircularProgress color="primary" size={50} style={{}} />
                  </div>
                ) : selectedProduct.img_src ? (
                  <img
                    src={selectedProduct.img_src}
                    onError={(e) => {
                      e.target.src = fallbackImage;
                    }}
                    style={{
                      boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                      borderRadius: "10px",
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
                      borderRadius: "10px",
                      width: "120px",
                      height: "120px",
                      objectFit: "cover",
                      cursor: "pointer",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <FileUploadIcon
                      style={{ color: "lightgrey", fontSize: "50px" }}
                    />
                  </div>
                )}
              </div>
              <div style={{ width: "100%" }}>
                <div>
                  <input
                    type="text"
                    name="name"
                    value={selectedProduct.name}
                    onChange={handleChange}
                    style={{
                      fontSize: "40px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "100%",
                      color: "rgba(0,0,0,0.7)",
                      fontweight: "600",
                      padding: "5px",
                    }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <div style={{ marginRight: "5px" }}>SGD </div>
                  <input
                    type="text"
                    name="unit_price"
                    value={selectedProduct.unit_price}
                    onChange={handleChange}
                    style={{
                      fontSize: "30px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "100px",
                      color: "rgba(0,0,0,0.7)",
                      marginTop: "15px",
                      fontweight: "600",
                      padding: "5px 10px",
                    }}
                  />
                </div>
                <div>
                  <textarea
                    type="text"
                    name="description"
                    value={selectedProduct.description}
                    onChange={handleChange}
                    style={{
                      fontSize: "15px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "100%",
                      color: "rgba(0,0,0,0.7)",
                      fontweight: "400",
                      marginTop: "15px",
                      height: "210px",
                      padding: "5px",
                    }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: "5px", width: "180px" }}>
                    Stock:
                  </div>
                  <input
                    type="text"
                    name="stock"
                    value={selectedProduct.stock}
                    onChange={handleChange}
                    style={{
                      fontSize: "15px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "100px",
                      color: "rgba(0,0,0,0.7)",
                      fontweight: "400",
                      marginTop: "15px",
                      padding: "5px",
                      marginRight: "5px",
                    }}
                  />
                  <div
                    className="blink"
                    style={{
                      width: "10px",
                      height: "10px",
                      background:
                        selectedProduct.stock < 10 ? "red" : "#0d8c22",
                      borderRadius: "50%",
                      boxShadow:
                        selectedProduct.stock < 10
                          ? "0px 0px 8px red"
                          : "0px 0px 8px #0d8c22",
                    }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: "5px", width: "180px" }}>
                    Color/Luminance:
                  </div>
                  <select
                    type="text"
                    name="luminance"
                    value={selectedProduct.luminance}
                    onChange={handleChange}
                    style={{
                      fontSize: "15px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "250px",
                      color: "rgba(0,0,0,0.7)",
                      fontweight: "400",
                      marginTop: "15px",
                      padding: "5px",
                    }}
                  >
                    <option value="None">None</option>
                    <option value="Blue">9,000 K</option>
                    <option value="Lightblue">6,500 K</option>
                    <option value="white">5,000 K</option>
                    <option value="Lightyellow">4,100 K</option>
                    <option value="Yellow">3,000 K</option>
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: "5px", width: "180px" }}>
                    Categories:
                  </div>
                  <input
                    type="text"
                    name="categories"
                    value={selectedProduct.categories}
                    onChange={handleChange}
                    style={{
                      fontSize: "15px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "600px",
                      color: "rgba(0,0,0,0.7)",
                      fontweight: "400",
                      marginTop: "15px",
                      padding: "5px",
                    }}
                  />
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <div style={{ marginRight: "5px", width: "180px" }}>
                    Tags:
                  </div>
                  <input
                    type="text"
                    name="tags"
                    value={selectedProduct.tags}
                    onChange={handleChange}
                    style={{
                      fontSize: "15px",
                      background: "none",
                      border: "1px solid lightgrey",
                      outline: "none",
                      borderRadius: "10px",
                      width: "600px",
                      color: "rgba(0,0,0,0.7)",
                      fontweight: "400",
                      marginTop: "15px",
                      padding: "5px",
                    }}
                  />
                </div>
                <button
                  style={{
                    border: "1.5px solid #d1ae6e",
                    color:
                      process.env.REACT_APP_TYPE !== "OASIS"
                        ? "rgb(209, 174, 110)"
                        : "rgb(191, 19, 42)",
                    padding: "8px 25px",
                    cursor: "pointer",
                    borderRadius: "10px",
                    outline: "none",
                    fontSize: "18px",
                    marginTop: "40px",
                    marginRight: "10px",
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    setDeleteIsOpen(true);
                  }}
                >
                  Delete
                </button>
                <button
                  type="submit"
                  style={{
                    background:
                      process.env.REACT_APP_TYPE !== "OASIS"
                        ? "rgb(209, 174, 110)"
                        : "rgb(191, 19, 42)",
                    padding: "8px 25px",
                    color: "white",
                    cursor: "pointer",
                    borderRadius: "10px",
                    border: "none",
                    outline: "none",
                    fontSize: "18px",
                    marginTop: "40px",
                  }}
                >
                  Save
                </button>
                {process.env.REACT_APP_TYPE !== "OASIS" && (
                  <div
                    style={{
                      marginTop: "20px",
                    }}
                  >
                    Navison Status:{" "}
                    {updatedNavisonStatus !== ""
                      ? updatedNavisonStatus
                      : selectedProduct.navisonStatus ?? "N/A"}
                  </div>
                )}
                {process.env.REACT_APP_TYPE !==
                  "OASIS" **
                  (
                    <button
                      style={{
                        border: "1.5px solid #d1ae6e",
                        color:
                          process.env.REACT_APP_TYPE !== "OASIS"
                            ? "rgb(209, 174, 110)"
                            : "rgb(191, 19, 42)",
                        padding: "8px 25px",
                        cursor: "pointer",
                        borderRadius: "10px",
                        outline: "none",
                        fontSize: "18px",
                        marginTop: "10px",
                        marginRight: "10px",
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSyncProduct();
                      }}
                    >
                      Sync Again
                    </button>
                  )}
              </div>
            </div>
          </form>
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            borderTop: "1px solid lightgrey",
            padding: "15px",
            background: "#f7f6f5",
            height: "calc(100vh - 270px)",
            overflowY: "scroll",
          }}
        >
          <div
            style={{
              border: "1px solid lightgrey",
              width: "calc(98% - 30px)",
              height: "35px",
              background: "white",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0 1%",
            }}
          >
            <div style={{ width: "10%" }}>Productss</div>
            <div style={{ width: "39%" }}>Product Description</div>
            <div style={{ width: "16%" }}>SKU</div>
            <div style={{ width: "16%" }}>Stock Status</div>
            <div style={{ width: "10%" }}>Categories</div>
            <div style={{ width: "10%" }}>Tags</div>
          </div>
          {paginatedProducts.map((product, index) => (
            <div
              className="invoice_item"
              key={index}
              style={{
                borderLeft: "1px solid lightgrey",
                borderBottom: "1px solid lightgrey",
                borderRight: "1px solid lightgrey",
                width: "calc(98% - 30px)",
                background: "rgba(256,256,256,0.8)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "5px 1%",
              }}
              onClick={() => {
                setUpdatedNavisonStatus("");
                setSelectedProduct(product);
                setIsFormVisible(true);
              }}
            >
              <div
                style={{
                  width: "10%",
                  display: "flex",
                  alignItems: "center",
                  paddingRight: "1%",
                }}
              >
                <img
                  src={product.img_src}
                  onError={(e) => {
                    e.target.src = fallbackImage;
                  }}
                  style={{
                    height: "80px",
                    width: "80px",
                    objectFit: "cover",
                    border: "1px solid lightgrey",
                    borderRadius: "10px",
                    marginRight: "5px",
                  }}
                />
              </div>
              <div style={{ width: "39%", paddingRight: "1%" }}>
                <div
                  style={{
                    fontweight: "600",
                    color:
                      process.env.REACT_APP_TYPE !== "OASIS"
                        ? "rgb(209, 174, 110)"
                        : "rgb(191, 19, 42)",
                  }}
                >
                  {product.name}
                </div>
                <div>
                  {product.description
                    ? product.description.slice(0, 70) +
                      (product.description.length > 70 ? " ..." : "")
                    : "None"}{" "}
                </div>
              </div>
              <div
                style={{
                  width: "16%",
                  paddingRight: "1%",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <div>
                  {product.sku
                    ? product.sku.slice(0, 10) +
                      (product.sku.length > 10 ? " ..." : "")
                    : "None"}
                </div>
              </div>
              <div
                style={{ width: "16%", paddingRight: "1%", display: "flex" }}
              >
                {product.stock > 10 ? (
                  <div style={{ color: "#0d8c22", fontweight: "600" }}>
                    In stock
                  </div>
                ) : product.stock <= 10 && product.stock > 0 ? (
                  <div style={{ color: "#e62e2e", fontweight: "600" }}>
                    Low stock
                  </div>
                ) : product.stock === 0 ? (
                  <div style={{ color: "#e62e2e", fontweight: "600" }}>
                    No stock
                  </div>
                ) : (
                  <></>
                )}

                <div style={{ marginLeft: "5px" }}>({product.stock})</div>
              </div>
              <div style={{ width: "9%", paddingRight: "1%" }}>
                {product.categories}
              </div>

              <div style={{ width: "9%", paddingRight: "1%" }}>
                {product.tags}
              </div>
            </div>
          ))}

          <Pagination
            pageNumber={currentPage}
            paginatedProperties={paginatedProperties}
            handlePagination={(page) => {
              setCurrentPage(page);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Product;
